import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { createCartCheckout, fetchShippingRates } from '../api';
import paymentGateway from '../services/paymentGateway';
import './CheckoutPage.css';

// Keep these in sync with config/commerce.php on the backend. The backend is the
// source of truth for what is charged — these are for display only.
const FREE_SHIPPING_THRESHOLD = 50;
const FLAT_SHIPPING_RATE = 9.99;
const TAX_RATE = 0.08;

const EMPTY_FORM = {
  customer_first_name: '',
  customer_last_name: '',
  customer_email: '',
  customer_phone: '',
  ship_to_name: '',
  ship_to_company: '',
  ship_to_line1: '',
  ship_to_line2: '',
  ship_to_city: '',
  ship_to_state: '',
  ship_to_postal_code: '',
  ship_to_country: 'US',
  customer_notes: '',
};

const formatPrice = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? '0.00' : num.toFixed(2);
};

const CheckoutPage = () => {
  const { items, total, clearCart } = useCart();

  const [form, setForm] = useState(EMPTY_FORM);
  const [shippingRates, setShippingRates] = useState([]);
  const [shippingService, setShippingService] = useState('');
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesNotice, setRatesNotice] = useState('');
  const [paymentCard, setPaymentCard] = useState(null);
  const [paymentProvider, setPaymentProvider] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const cardContainerRef = useRef(null);

  const subtotal = total;
  const lineTotal = (item) => (parseFloat(item.price) || 0) * item.quantity;
  const shippableSubtotal = items
    .filter((item) => item.itemType !== 'donation' || item.shippingRequired)
    .reduce((sum, item) => sum + lineTotal(item), 0);
  const taxableSubtotal = items
    .filter((item) => item.itemType !== 'donation' || item.taxable)
    .reduce((sum, item) => sum + lineTotal(item), 0);
  const requiresShipping = shippableSubtotal > 0;
  const selectedRate = shippingRates.find((rate) => rate.service === shippingService);
  // Until a rate is quoted, show the flat rate as an estimate rather than a
  // confident zero. The server prices the order either way.
  const shipping = !requiresShipping
    ? 0
    : selectedRate
      ? selectedRate.amount
      : (shippableSubtotal >= FREE_SHIPPING_THRESHOLD ? 0 : FLAT_SHIPPING_RATE);
  const tax = taxableSubtotal * TAX_RATE;
  const finalTotal = subtotal + shipping + tax;

  // Mount the secure card field for whichever gateway the backend has live.
  // The service loads the right SDK itself, so nothing here is Square- or
  // Stripe-specific.
  useEffect(() => {
    if (paymentCard || confirmedOrder || items.length === 0 || !cardContainerRef.current) {
      return undefined;
    }

    let cancelled = false;

    const initCard = async () => {
      try {
        const handle = await paymentGateway.mountCard('checkout-card-container');
        if (cancelled) {
          paymentGateway.destroyCard(handle);
          return;
        }
        setPaymentCard(handle);
        setPaymentProvider(handle.provider);
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            error.message || 'We could not load the secure payment form. Please refresh and try again.'
          );
        }
      }
    };

    initCard();

    return () => {
      cancelled = true;
    };
  }, [paymentCard, confirmedOrder, items.length]);

  // Re-quote whenever the destination changes. Debounced, because a customer
  // types a ZIP one digit at a time and each keystroke would otherwise be a
  // carrier call.
  useEffect(() => {
    if (!requiresShipping || confirmedOrder) return undefined;

    const shippableItems = items
      .filter((item) => item.itemType !== 'donation')
      .map((item) => ({ product_id: item.productId, quantity: item.quantity }));

    if (!shippableItems.length || !form.ship_to_postal_code || !form.ship_to_country) {
      setShippingRates([]);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setRatesLoading(true);
      try {
        const result = await fetchShippingRates({
          items: shippableItems,
          destination: {
            postal_code: form.ship_to_postal_code,
            country: form.ship_to_country,
            state: form.ship_to_state,
            city: form.ship_to_city,
          },
        });
        if (cancelled) return;

        const rates = result.rates || [];
        setShippingRates(rates);
        setRatesNotice(result.message || '');
        // Keep the customer's choice if it survived the re-quote; otherwise
        // default to the cheapest so a total is always shown.
        setShippingService((current) => {
          if (rates.some((rate) => rate.service === current)) return current;
          return rates.length ? [...rates].sort((a, b) => a.amount - b.amount)[0].service : '';
        });
      } catch (error) {
        if (!cancelled) {
          setShippingRates([]);
          setRatesNotice('We could not load live rates; a standard rate will be applied.');
        }
      } finally {
        if (!cancelled) setRatesLoading(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    requiresShipping, confirmedOrder, items,
    form.ship_to_postal_code, form.ship_to_country, form.ship_to_state, form.ship_to_city,
  ]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!paymentCard) {
      setErrorMessage('The payment form is still loading. Please wait a moment.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const token = await paymentGateway.tokenize(paymentCard);

      const payload = {
        ...form,
        ship_to_country: (form.ship_to_country || 'US').toUpperCase(),
        shipping_service: shippingService || null,
        source_id: token,
        items: items.map((item) => ({
          type: item.itemType || 'product',
          product_id: item.itemType === 'donation' ? null : item.productId,
          product_variant_id: item.variantId ?? null,
          donation_card_id: item.donationCardId,
          donation_card_slug: item.donationCardSlug,
          selected_options: item.selectedOptions || null,
          quantity: item.quantity,
        })),
      };

      const result = await createCartCheckout(payload);

      clearCart();
      setConfirmedOrder(result.order);
    } catch (error) {
      setErrorMessage(error.message || 'We could not complete your order.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Order confirmation view.
  if (confirmedOrder) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="order-confirmation">
            <div className="confirmation-icon">
              <svg width="72" height="72" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <h1>Thank you for your order!</h1>
            <p className="confirmation-number">
              Order <strong>{confirmedOrder.order_number}</strong>
            </p>
            <p>A confirmation has been recorded for {confirmedOrder.items?.length || 0} item
              {confirmedOrder.items?.length !== 1 ? 's' : ''}. Total charged:{' '}
              <strong>${formatPrice(confirmedOrder.total)} {confirmedOrder.currency}</strong>.
            </p>
            {confirmedOrder.receipt_url && (
              <a
                className="receipt-link"
                href={confirmedOrder.receipt_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Square receipt
              </a>
            )}
            <Link to="/merch" className="continue-shopping-btn">Continue Shopping</Link>
          </div>
        </div>
      </div>
    );
  }

  // Empty cart guard.
  if (items.length === 0) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="empty-checkout">
            <h1>Your cart is empty</h1>
            <p>Add something to your cart before checking out.</p>
            <Link to="/merch" className="continue-shopping-btn">Browse Merch</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-header">
          <div className="breadcrumb">
            <Link to="/cart">Cart</Link>
            <span className="breadcrumb-separator">›</span>
            <span>Checkout</span>
          </div>
          <h1>Checkout</h1>
        </div>

        {errorMessage && (
          <div className="checkout-alert" role="alert">{errorMessage}</div>
        )}

        <div className="checkout-content">
          <form className="checkout-form" onSubmit={handleSubmit}>
            <section className="form-section">
              <h2>Contact</h2>
              <div className="form-row">
                <label>
                  First name*
                  <input name="customer_first_name" value={form.customer_first_name} onChange={handleChange} required />
                </label>
                <label>
                  Last name*
                  <input name="customer_last_name" value={form.customer_last_name} onChange={handleChange} required />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Email*
                  <input type="email" name="customer_email" value={form.customer_email} onChange={handleChange} required />
                </label>
                <label>
                  Phone
                  <input name="customer_phone" value={form.customer_phone} onChange={handleChange} />
                </label>
              </div>
            </section>

            <section className="form-section">
              <h2>{requiresShipping ? 'Shipping address' : 'Support details'}</h2>
              {!requiresShipping && (
                <p className="payment-hint">No shipping address is needed for this cart.</p>
              )}
              {requiresShipping && (
                <>
              <label>
                Full name*
                <input name="ship_to_name" value={form.ship_to_name} onChange={handleChange} required />
              </label>
              <label>
                Company
                <input name="ship_to_company" value={form.ship_to_company} onChange={handleChange} />
              </label>
              <label>
                Address line 1*
                <input name="ship_to_line1" value={form.ship_to_line1} onChange={handleChange} required />
              </label>
              <label>
                Address line 2
                <input name="ship_to_line2" value={form.ship_to_line2} onChange={handleChange} />
              </label>
              <div className="form-row">
                <label>
                  City*
                  <input name="ship_to_city" value={form.ship_to_city} onChange={handleChange} required />
                </label>
                <label>
                  State / Region
                  <input name="ship_to_state" value={form.ship_to_state} onChange={handleChange} />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Postal code*
                  <input name="ship_to_postal_code" value={form.ship_to_postal_code} onChange={handleChange} required />
                </label>
                <label>
                  Country* (2-letter)
                  <input
                    name="ship_to_country"
                    value={form.ship_to_country}
                    onChange={handleChange}
                    maxLength={2}
                    required
                  />
                </label>
              </div>

              <fieldset className="shipping-methods">
                <legend>Shipping method</legend>
                {ratesLoading && <p className="payment-hint">Checking rates…</p>}
                {!ratesLoading && shippingRates.length === 0 && (
                  <p className="payment-hint">
                    {ratesNotice || 'Enter your postal code and country to see shipping options.'}
                  </p>
                )}
                {!ratesLoading && shippingRates.map((rate) => (
                  <label key={rate.service} className={`shipping-method${shippingService === rate.service ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="shipping_service"
                      value={rate.service}
                      checked={shippingService === rate.service}
                      onChange={() => setShippingService(rate.service)}
                    />
                    <span className="shipping-method__body">
                      <span className="shipping-method__label">{rate.label}</span>
                      {rate.estimate && <span className="shipping-method__estimate">{rate.estimate}</span>}
                    </span>
                    <span className="shipping-method__price">
                      {rate.amount === 0 ? 'FREE' : `$${formatPrice(rate.amount)}`}
                    </span>
                  </label>
                ))}
                {ratesNotice && shippingRates.length > 0 && (
                  <p className="payment-hint">{ratesNotice}</p>
                )}
              </fieldset>
                </>
              )}
              <label>
                Order notes
                <textarea name="customer_notes" value={form.customer_notes} onChange={handleChange} rows={3} />
              </label>
            </section>

            <section className="form-section">
              <h2>Payment</h2>
              <p className="payment-hint">
                {paymentProvider === 'stripe' ? 'Powered by Stripe.' : 'Powered by Square.'}
                {' '}Your card details are handled securely.
              </p>
              <div id="checkout-card-container" className="card-container" ref={cardContainerRef} />
            </section>

            <button type="submit" className="place-order-btn" disabled={isSubmitting || !paymentCard}>
              {isSubmitting
                ? 'Processing…'
                : paymentCard
                  ? `Pay $${formatPrice(finalTotal)}`
                  : 'Loading secure form…'}
            </button>
          </form>

          <aside className="checkout-summary">
            <h2>Order Summary</h2>
            <div className="summary-items">
              {items.map((item) => (
                <div key={item.id} className="summary-item">
                  <div className="summary-item-main">
                    <span className="summary-item-name">{item.name}</span>
                    {item.itemType === 'donation' && (
                      <span className="summary-item-variant">One-time Guhso support</span>
                    )}
                    {item.variant && (
                      <span className="summary-item-variant">
                        {[item.variant.color, item.variant.size].filter(Boolean).join(' / ')}
                      </span>
                    )}
                    <span className="summary-item-qty">Qty {item.quantity}</span>
                  </div>
                  <span className="summary-item-price">
                    ${formatPrice((parseFloat(item.price) || 0) * item.quantity)}
                  </span>
                </div>
              ))}
            </div>

            <div className="summary-row">
              <span>Subtotal</span>
              <span>${formatPrice(subtotal)}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span>{!requiresShipping ? 'Not required' : shipping === 0 ? 'FREE' : `$${formatPrice(shipping)}`}</span>
            </div>
            <div className="summary-row">
              <span>Tax</span>
              <span>${formatPrice(tax)}</span>
            </div>
            <div className="summary-divider" />
            <div className="summary-row summary-total">
              <span>Total</span>
              <span>${formatPrice(finalTotal)}</span>
            </div>

            <Link to="/cart" className="back-to-cart">← Back to cart</Link>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
